/**
 * Maps Firestore subscription data + resolved meta/prices to redesign tier cards.
 */

import type { DevTier } from "@/lib/dev-state";
import type {
  FirestoreUserMetaAdvancedSubscriptions,
  MembershipPricesResolution,
  ResolvedMembershipPlanIds,
  ResolvedMembershipPrices,
} from "@/lib/firebase";

export const ROSS_CREATIONS_CREATOR_ID = "JG4t6p569t587twlVq8w";

/** Map dev switcher tier to subscription creator scope (matches checkout). */
export function resolveDevCreators(tier: DevTier): "all" | string[] {
  if (tier === "basic" || tier === "library-only") return [ROSS_CREATIONS_CREATOR_ID];
  return "all";
}

export type MembershipTierName = "Basic" | "Plus" | "Super Sub" | "Library Only";
export type MembershipBilling = "monthly" | "yearly";
export type V2MembershipTier = "basic" | "plus" | "super-sub";

/** Map membership card name to v2 video-access tier. */
export function resolveV2TierFromTierName(name: string): V2MembershipTier | null {
  const normalized = name.trim().toLowerCase();
  if (normalized === "basic") return "basic";
  if (normalized === "plus") return "plus";
  if (normalized === "super sub" || normalized === "supersub") return "super-sub";
  return null;
}

/** Creator scope for `subscribeCreator` / `updateSubscription`. */
export function tierCreatorsForCheckout(tierName: string): "all" | string[] {
  const tier = resolveV2TierFromTierName(tierName);
  if (tier === "basic") return [ROSS_CREATIONS_CREATOR_ID];
  if (tier) return "all";
  return [ROSS_CREATIONS_CREATOR_ID];
}

export const LIBRARY_ONLY_PLAN_ID = "library-only";
export const LIBRARY_ONLY_PRICE_ROW_ID = "library-only-v2";

/** Whether a subscription document represents library-only retention. */
export function isLibraryOnlyPlan(plan?: string): boolean {
  return plan === LIBRARY_ONLY_PLAN_ID;
}

export function isLibraryOnlySubscription(
  subscription: MembershipSubscription | null | undefined,
): boolean {
  if (!subscription) return false;
  if (isLibraryOnlyPlan(subscription.plan)) return true;
  return subscription.priceLookupId === LIBRARY_ONLY_PRICE_ROW_ID;
}

export function hasPendingLibraryOnlySwitch(
  subscription: MembershipSubscription | null | undefined,
): boolean {
  return isLibraryOnlyPlan(subscription?.scheduledChange?.plan);
}

function resolveLibraryOnlyMembership(
  subscription: MembershipSubscription,
): ResolvedCurrentMembership {
  return {
    tierName: "Library Only",
    billing: "monthly",
    priceKey: "monthly",
    planId: LIBRARY_ONLY_PRICE_ROW_ID,
    subscriptionId: subscription.id,
    referralCreator:
      subscription.referralCreator ?? ROSS_CREATIONS_CREATOR_ID,
    isLegacy: false,
  };
}

export type MembershipSubscription = {
  id: string;
  status: string;
  type?: string;
  creators: "all" | string[];
  plan?: string;
  v2Tier?: V2MembershipTier;
  priceLookupId?: string;
  renewPrice?: number;
  referralCreator?: string;
  renewing?: boolean;
  renewsAt?: { toDate?: () => Date; toMillis?: () => number; seconds?: number };
  coupon?: {
    id: string;
    expiresAt?: { toDate?: () => Date; toMillis?: () => number; seconds?: number };
  };
  scheduledChange?: {
    plan?: string;
    creators?: "all" | string[];
    referralCreator?: string;
  };
};

export type ResolvedCurrentMembership = {
  tierName: MembershipTierName;
  billing: MembershipBilling;
  priceKey: keyof ResolvedMembershipPrices;
  planId: string;
  subscriptionId: string;
  referralCreator: string;
  isLegacy: false;
};

export type LegacyMembership = {
  isLegacy: true;
  subscriptionId: string;
  referralCreator: string;
  plan: string;
};

export type CurrentMembershipResult =
  | ResolvedCurrentMembership
  | LegacyMembership
  | null;

const ACTIVE_STATUSES = new Set(["active", "trialing"]);
const PRICE_TOLERANCE = 1;

const DEV_TIER_TO_NAME: Record<DevTier, MembershipTierName> = {
  basic: "Basic",
  plus: "Plus",
  vip: "Super Sub",
  "library-only": "Library Only",
};

const DEV_TIER_PRICE_KEYS: Record<
  DevTier,
  { monthly: keyof ResolvedMembershipPrices; yearly?: keyof ResolvedMembershipPrices }
> = {
  basic: { monthly: "monthly", yearly: "yearly" },
  plus: { monthly: "plusMonthly", yearly: "plusYearly" },
  vip: { monthly: "superSub", yearly: "superSubYearly" },
  "library-only": { monthly: "monthly" },
};

const V2_TIER_TO_NAME: Record<V2MembershipTier, MembershipTierName> = {
  basic: "Basic",
  plus: "Plus",
  "super-sub": "Super Sub",
};

const V2_TIER_PRICE_KEYS: Record<
  V2MembershipTier,
  { monthly: keyof ResolvedMembershipPrices; yearly?: keyof ResolvedMembershipPrices }
> = {
  basic: { monthly: "monthly", yearly: "yearly" },
  plus: { monthly: "plusMonthly", yearly: "plusYearly" },
  "super-sub": { monthly: "superSub", yearly: "superSubYearly" },
};

function pricesMatch(a: number, b: number): boolean {
  return Math.abs(a - b) <= PRICE_TOLERANCE;
}

/** Pick the active Real Pranks subscription used for plan management. */
export function findActiveSubscription(
  subs: FirestoreUserMetaAdvancedSubscriptions | null | undefined,
): MembershipSubscription | null {
  if (!subs?.subscriptions?.length) return null;

  const active = subs.subscriptions.filter((s) =>
    ACTIVE_STATUSES.has(s.status),
  ) as MembershipSubscription[];

  if (!active.length) return null;

  const allCreators = active.find((s) => s.creators === "all");
  if (allCreators) return allCreators;

  const ross = active.find(
    (s) =>
      Array.isArray(s.creators) &&
      s.creators.includes(ROSS_CREATIONS_CREATOR_ID),
  );
  return ross ?? active[0] ?? null;
}

/** Active library-only member, including localhost dev preview tier. */
export function isLibraryOnlyMember(
  subscriptions: FirestoreUserMetaAdvancedSubscriptions | null | undefined,
  devPreview?: { enabled: boolean; tier: DevTier } | null,
): boolean {
  if (devPreview?.enabled && devPreview.tier === "library-only") return true;
  return isLibraryOnlySubscription(findActiveSubscription(subscriptions));
}

type PriceMatch = {
  tierName: MembershipTierName;
  billing: MembershipBilling;
  priceKey: keyof ResolvedMembershipPrices;
};

function matchTierFromRenewPrice(
  renewPrice: number,
  stripePlan: string,
  prices: ResolvedMembershipPrices,
): PriceMatch | null {
  if (stripePlan === "super-sub") {
    if (
      prices.superSubYearly != null &&
      pricesMatch(renewPrice, prices.superSubYearly)
    ) {
      return {
        tierName: "Super Sub",
        billing: "yearly",
        priceKey: "superSubYearly",
      };
    }
    if (pricesMatch(renewPrice, prices.superSub)) {
      return { tierName: "Super Sub", billing: "monthly", priceKey: "superSub" };
    }
    return { tierName: "Super Sub", billing: "monthly", priceKey: "superSub" };
  }

  if (stripePlan === "all-creators") {
    const candidates: PriceMatch[] = [
      { tierName: "Plus", billing: "yearly", priceKey: "plusYearly" },
      { tierName: "Plus", billing: "monthly", priceKey: "plusMonthly" },
      { tierName: "Basic", billing: "yearly", priceKey: "yearly" },
      { tierName: "Basic", billing: "monthly", priceKey: "monthly" },
    ];
    for (const c of candidates) {
      const amount = prices[c.priceKey];
      if (amount != null && pricesMatch(renewPrice, amount)) return c;
    }
    return { tierName: "Basic", billing: "monthly", priceKey: "monthly" };
  }

  return null;
}

function planIdForKey(
  planIds: ResolvedMembershipPlanIds,
  priceKey: keyof ResolvedMembershipPrices,
): string | null {
  const id = planIds[priceKey as keyof ResolvedMembershipPlanIds];
  if (typeof id === "string" && id) return id;
  if (priceKey === "yearly" && planIds.yearly) return planIds.yearly;
  return null;
}

/** Resolve redesign tier card from backend-stamped {@link V2MembershipTier}. */
function resolveMembershipFromV2Tier(
  subscription: MembershipSubscription,
  v2Tier: V2MembershipTier,
  resolution: Extract<MembershipPricesResolution, { ok: true }>,
): ResolvedCurrentMembership | null {
  const keys = V2_TIER_PRICE_KEYS[v2Tier];
  let billing: MembershipBilling = "monthly";
  let priceKey: keyof ResolvedMembershipPrices = keys.monthly;

  const renewPrice = subscription.renewPrice;
  if (renewPrice != null && keys.yearly) {
    const yearlyAmount = resolution.prices[keys.yearly];
    if (yearlyAmount != null && pricesMatch(renewPrice, yearlyAmount)) {
      billing = "yearly";
      priceKey = keys.yearly;
    }
  }

  const planId = planIdForKey(resolution.planIds, priceKey);
  if (!planId) return null;

  return {
    tierName: V2_TIER_TO_NAME[v2Tier],
    billing,
    priceKey,
    planId,
    subscriptionId: subscription.id,
    referralCreator:
      subscription.referralCreator ?? ROSS_CREATIONS_CREATOR_ID,
    isLegacy: false,
  };
}

/** Resolve dev-switcher preview tier (non-real auth mode). */
export function resolveDevMembershipTier(
  devTier: DevTier,
  billing: MembershipBilling,
  resolution: Extract<MembershipPricesResolution, { ok: true }>,
): ResolvedCurrentMembership | null {
  if (devTier === "library-only") {
    return {
      tierName: "Library Only",
      billing: "monthly",
      priceKey: "monthly",
      planId: LIBRARY_ONLY_PRICE_ROW_ID,
      subscriptionId: "dev-preview",
      referralCreator: ROSS_CREATIONS_CREATOR_ID,
      isLegacy: false,
    };
  }

  const keys = DEV_TIER_PRICE_KEYS[devTier];
  const priceKey =
    billing === "yearly" && keys.yearly ? keys.yearly : keys.monthly;
  const planId = planIdForKey(resolution.planIds, priceKey);
  if (!planId) return null;

  return {
    tierName: DEV_TIER_TO_NAME[devTier],
    billing,
    priceKey,
    planId,
    subscriptionId: "dev-preview",
    referralCreator: ROSS_CREATIONS_CREATOR_ID,
    isLegacy: false,
  };
}

/** Map an active subscription + prices to a redesign tier card, or legacy/null. */
export function resolveCurrentMembershipTier(
  subscription: MembershipSubscription | null,
  resolution: Extract<MembershipPricesResolution, { ok: true }> | null,
): CurrentMembershipResult {
  if (!subscription || !resolution) return null;

  if (isLibraryOnlySubscription(subscription)) {
    return resolveLibraryOnlyMembership(subscription);
  }

  const stripePlan = subscription.plan ?? "";
  const renewPrice = subscription.renewPrice;

  if (subscription.v2Tier) {
    return resolveMembershipFromV2Tier(
      subscription,
      subscription.v2Tier,
      resolution,
    );
  }

  if (
    stripePlan !== "all-creators" &&
    stripePlan !== "super-sub"
  ) {
    return {
      isLegacy: true,
      subscriptionId: subscription.id,
      referralCreator:
        subscription.referralCreator ?? ROSS_CREATIONS_CREATOR_ID,
      plan: stripePlan,
    };
  }

  const match =
    renewPrice != null
      ? matchTierFromRenewPrice(renewPrice, stripePlan, resolution.prices)
      : stripePlan === "super-sub"
        ? ({
            tierName: "Super Sub",
            billing: "monthly",
            priceKey: "superSub",
          } satisfies PriceMatch)
        : ({
            tierName: "Basic",
            billing: "monthly",
            priceKey: "monthly",
          } satisfies PriceMatch);

  if (!match) return null;

  const planId = planIdForKey(resolution.planIds, match.priceKey);
  if (!planId) return null;

  return {
    tierName: match.tierName,
    billing: match.billing,
    priceKey: match.priceKey,
    planId,
    subscriptionId: subscription.id,
    referralCreator:
      subscription.referralCreator ?? ROSS_CREATIONS_CREATOR_ID,
    isLegacy: false,
  };
}

/** Resolve scheduled upcoming tier for banner copy. */
export function resolveScheduledMembershipTier(
  subscription: MembershipSubscription | null,
  resolution: Extract<MembershipPricesResolution, { ok: true }> | null,
): { tierName: MembershipTierName; billing: MembershipBilling } | null {
  if (!subscription?.scheduledChange?.plan || !resolution) return null;

  if (isLibraryOnlyPlan(subscription.scheduledChange.plan)) {
    return { tierName: "Library Only", billing: "monthly" };
  }

  const synthetic: MembershipSubscription = {
    ...subscription,
    plan: subscription.scheduledChange.plan,
    v2Tier: undefined,
    priceLookupId: undefined,
    renewPrice: undefined,
  };
  const resolved = resolveCurrentMembershipTier(synthetic, resolution);
  if (!resolved || resolved.isLegacy) return null;
  return { tierName: resolved.tierName, billing: resolved.billing };
}

export function formatRenewalDate(
  renewsAt: MembershipSubscription["renewsAt"],
): string | null {
  if (!renewsAt) return null;
  const date =
    typeof renewsAt.toDate === "function"
      ? renewsAt.toDate()
      : renewsAt.seconds != null
        ? new Date(renewsAt.seconds * 1000)
        : null;
  if (!date) return null;
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Whether switching to `target` is an upgrade (immediate) vs downgrade (next cycle). */
export function isMembershipUpgrade(
  current: ResolvedCurrentMembership,
  target: { tierName: MembershipTierName; price: number },
  currentRenewPrice: number,
): boolean {
  const tierRank: Record<MembershipTierName, number> = {
    Basic: 0,
    Plus: 1,
    "Super Sub": 2,
    "Library Only": -1,
  };
  const currentRank = tierRank[current.tierName];
  const targetRank = tierRank[target.tierName];
  if (targetRank > currentRank) return true;
  if (targetRank < currentRank) return false;
  return target.price >= currentRenewPrice;
}

/** Resolve v2 tier from the active subscription document. */
export function resolveActiveV2Tier(
  subs: FirestoreUserMetaAdvancedSubscriptions | null | undefined,
  devTier?: DevTier,
  isDevMode?: boolean,
): V2MembershipTier | null {
  if (isDevMode && devTier) {
    if (devTier === "basic") return "basic";
    if (devTier === "plus") return "plus";
    if (devTier === "vip") return "super-sub";
  }
  const active = findActiveSubscription(subs) as MembershipSubscription | null;
  if (!active) return null;
  if (isLibraryOnlySubscription(active)) return null;
  if (active.v2Tier) return active.v2Tier;
  if (active.plan === "super-sub") return "super-sub";
  if (active.plan === "one-creator") return "basic";
  if (active.plan === "all-creators") {
    return active.priceLookupId?.includes("plus") ? "plus" : "basic";
  }
  return null;
}

export function isSuperSubMember(
  subs: FirestoreUserMetaAdvancedSubscriptions | null | undefined,
  devTier?: DevTier,
): boolean {
  if (devTier === "vip") return true;
  const active = findActiveSubscription(subs);
  return active?.plan === "super-sub" && ACTIVE_STATUSES.has(active.status);
}
